import React, { useState } from "react";
import { motion } from "framer-motion";
import { LEARN } from "../../theme";
import { useBreakpoint } from "../../shell/LearnShell";
import { COUNTING_ENGINE_THEME, countingCompareSizes } from "../../../../data/countingLadderCatalog";
import { ConfettiBurst } from "../../feedback/Celebration";
import {
  CARD_FACE, CheckIcon, CountingPanel, CountingPiece, CountingPieceGrid, CountingRoot, DRAG_BUTTON, EDGE_IDLE, POP_SPRING, ShakeWrap, hexAlpha,
  useCompleteOnce, useCountingDrag, useWrongFlash,
} from "../CountingPiece";

const SMALL = "group-small";
const LARGE = "group-large";
const DATA_SMALL = { side: "small" };
const DATA_LARGE = { side: "large" };

/** The little "vs" coin between the two groups. */
function VersusCoin({ accent }) {
  return (
    <div
      aria-hidden="true"
      style={{
        width: "calc(var(--tile) * 0.62)", height: "calc(var(--tile) * 0.62)", borderRadius: "50%", flexShrink: 0, alignSelf: "center",
        display: "grid", placeItems: "center", background: "#fff", border: `3px solid ${hexAlpha(accent, 0.5)}`,
        boxShadow: `0 4px 0 ${hexAlpha(accent, 0.45)}, 0 8px 16px rgba(11,26,51,0.12)`,
        fontFamily: LEARN.font, fontWeight: 700, fontSize: "clamp(15px, calc(var(--tile) * 0.2), 20px)", color: LEARN.color.muted, letterSpacing: "0.02em",
      }}
    >
      vs
    </div>
  );
}

/** Mint medal that pops onto the winning pane. */
function WinMedal({ reduceMotion }) {
  return (
    <motion.span
      aria-hidden="true"
      initial={reduceMotion ? false : { scale: 0, rotate: -30 }}
      animate={{ scale: 1, rotate: 0 }}
      transition={reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 480, damping: 16, delay: 0.1 }}
      style={{
        position: "absolute", top: "calc(var(--tile) * -0.18)", right: "calc(var(--tile) * -0.12)", zIndex: 3,
        width: "calc(var(--tile) * 0.56)", height: "calc(var(--tile) * 0.56)", borderRadius: "50%", background: LEARN.color.mint,
        border: "3px solid #fff", boxShadow: `0 4px 0 ${LEARN.color.mintDeep}, 0 8px 16px rgba(11,26,51,0.16)`, display: "grid", placeItems: "center",
      }}
    >
      <CheckIcon size={22} />
    </motion.span>
  );
}

/**
 * More or fewer: two groups side by side; tap the one with more. Activity
 * 72 turns the groups into balance pans: drag the block onto the lighter
 * pan so both sides match.
 */
const CompareSetsGame = ({ activity, sfx, onComplete, onWrong, reduceMotion = false }) => {
  const { larger, smaller } = countingCompareSizes(activity);
  const balance = activity.number === 72;
  const accent = COUNTING_ENGINE_THEME.compareSets.accent;
  const bp = useBreakpoint();
  const stacked = bp === "xs";
  const [solved, setSolved] = useState(false);
  const [blockSelected, setBlockSelected] = useState(false);
  const [burst, setBurst] = useState(0);
  const complete = useCompleteOnce(onComplete);
  const { flash, isWrong } = useWrongFlash();

  const win = () => {
    if (solved) return true;
    setSolved(true);
    setBlockSelected(false);
    sfx?.(balance ? "snap" : "pop");
    setTimeout(() => sfx?.("correct"), 140);
    setBurst((b) => b + 1);
    complete();
    return true;
  };

  const miss = (id) => {
    flash(id);
    onWrong?.();
    return false;
  };

  const dropBlock = (side) => (side === "small" ? win() : miss(LARGE));

  const { dragProps, registerTarget, hoverId, guardTap, GhostLayer } = useCountingDrag({
    reduceMotion,
    sfx,
    onDrop: ({ targetId, targetData }) => (targetId ? dropBlock(targetData.side) : false),
  });

  const onGroupTap = (side) => {
    if (solved) return;
    if (balance) {
      if (!blockSelected) return;
      dropBlock(side);
      return;
    }
    if (side === "large") win();
    else miss(SMALL);
  };

  const ghost = () => <CountingPiece asset="cube" variant="bare" size="100%" />;

  const renderGroup = ({ id, side, count, asset, data }) => {
    const hover = hoverId === id;
    const winner = solved && (balance ? side === "small" : side === "large");
    const armed = balance && blockSelected && !solved;
    return (
      <ShakeWrap key={id} shake={isWrong(id)} reduceMotion={reduceMotion} style={{ display: "flex", minWidth: 0, minHeight: 0, position: "relative" }}>
        <motion.button
          type="button"
          ref={balance ? (el) => registerTarget(id, el, data) : undefined}
          className="counting-btn counting-target"
          aria-label={`Group of ${count}`}
          onClick={guardTap(() => onGroupTap(side))}
          disabled={solved}
          whileTap={reduceMotion || solved ? undefined : { scale: 0.97, y: 3 }}
          whileHover={reduceMotion || solved || (balance && !blockSelected) ? undefined : { y: -3 }}
          animate={reduceMotion ? {} : winner ? { scale: [1, 1.07, 1.03] } : { scale: hover ? 1.04 : 1 }}
          transition={winner ? { duration: 0.5, ease: "easeOut" } : { type: "spring", stiffness: 300, damping: 22 }}
          style={{
            flex: 1, minWidth: 0, minHeight: "calc(var(--tile) * 2.1)", padding: "clamp(8px, 2vmin, 16px)", borderRadius: LEARN.radius.lg, boxSizing: "border-box", "--target-accent": accent,
            background: winner
              ? `linear-gradient(180deg, #fff, ${hexAlpha(LEARN.color.mint, 0.22)})`
              : hover ? `linear-gradient(180deg, #fff, ${hexAlpha(accent, 0.3)})` : CARD_FACE,
            border: `3px solid ${winner ? LEARN.color.mint : hover || armed ? accent : hexAlpha(accent, 0.4)}`,
            boxShadow: winner
              ? `inset 0 2px 0 rgba(255,255,255,0.9), 0 calc(var(--tile) * 0.08) 0 ${LEARN.color.mintDeep}, 0 calc(var(--tile) * 0.16) calc(var(--tile) * 0.26) ${hexAlpha(LEARN.color.ink, 0.14)}`
              : `inset 0 2px 0 rgba(255,255,255,0.9), 0 calc(var(--tile) * 0.08) 0 ${hover ? hexAlpha(accent, 0.8) : EDGE_IDLE}, 0 calc(var(--tile) * 0.14) calc(var(--tile) * 0.22) ${hexAlpha(LEARN.color.ink, 0.12)}`,
            cursor: solved ? "default" : balance && !blockSelected ? "default" : "pointer",
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8,
            transition: "background 160ms, border-color 160ms, box-shadow 160ms", touchAction: "manipulation", position: "relative", overflow: "visible",
          }}
        >
          <CountingPieceGrid count={count} asset={asset} reduceMotion={reduceMotion} labelPrefix={asset} />
          {balance && solved && side === "small" && (
            <motion.span initial={reduceMotion ? false : { scale: 0.4 }} animate={{ scale: 1 }} transition={POP_SPRING} style={{ display: "inline-flex", flexShrink: 0 }}>
              <CountingPiece asset="cube" variant="bare" size="calc(var(--tile) * 1.1)" reduceMotion={reduceMotion} />
            </motion.span>
          )}
        </motion.button>
        {winner && <WinMedal reduceMotion={reduceMotion} />}
      </ShakeWrap>
    );
  };

  return (
    <CountingRoot>
      <CountingPanel grow tint={accent} style={{ gap: 10 }}>
        {balance && (
          <div aria-hidden="true" style={{ width: "min(100%, calc(var(--tile) * 6))", display: "grid", placeItems: "center", marginBottom: -4 }}>
            <motion.div
              animate={{ rotate: solved ? 0 : 4 }}
              transition={reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 120, damping: 14 }}
              style={{ width: "100%", height: "calc(var(--tile) * 0.1)", borderRadius: 999, background: LEARN.color.ink, transformOrigin: "50% 50%", boxShadow: "0 3px 6px rgba(11,26,51,0.25)" }}
            />
            <div style={{ width: 0, height: 0, borderLeft: "calc(var(--tile) * 0.14) solid transparent", borderRight: "calc(var(--tile) * 0.14) solid transparent", borderBottom: `calc(var(--tile) * 0.18) solid ${LEARN.color.ink}`, marginTop: -2 }} />
          </div>
        )}
        <div
          style={{
            width: "min(100%, calc(var(--tile) * 8.5))", flex: "1 1 0%", minHeight: 0, display: "grid",
            gridTemplateColumns: stacked ? "minmax(0, 1fr)" : "minmax(0, 1fr) auto minmax(0, 1fr)",
            gridTemplateRows: stacked ? "minmax(0, 1fr) auto minmax(0, 1fr)" : "minmax(0, 1fr)",
            gap: "clamp(10px, 2vmin, 18px)", alignItems: "stretch", justifyItems: "stretch",
          }}
        >
          {renderGroup({ id: SMALL, side: "small", count: smaller, asset: "blueberry", data: DATA_SMALL })}
          {balance ? <div aria-hidden="true" style={{ width: stacked ? undefined : "calc(var(--tile) * 0.2)", height: stacked ? "calc(var(--tile) * 0.2)" : undefined }} /> : <VersusCoin accent={accent} />}
          {renderGroup({ id: LARGE, side: "large", count: larger, asset: "flower", data: DATA_LARGE })}
        </div>
        {burst > 0 && <ConfettiBurst key={`burst-${burst}`} count={28} originY={40} reduceMotion={reduceMotion} />}
      </CountingPanel>

      {balance && (
        <div style={{ display: "flex", justifyContent: "center", minHeight: "calc(var(--tile) * 1.4)", alignItems: "center" }}>
          {!solved && (() => {
            const dp = dragProps("cube", ghost);
            return (
              <motion.div initial={reduceMotion ? false : { scale: 0 }} animate={{ scale: 1, y: blockSelected ? -8 : 0 }} transition={POP_SPRING} style={{ display: "inline-flex" }}>
                <button
                  type="button"
                  {...dp}
                  aria-label="Block"
                  className="counting-btn"
                  onClick={guardTap(() => { sfx?.("pop"); setBlockSelected((s) => !s); })}
                  style={{ ...DRAG_BUTTON, ...dp.style }}
                >
                  <CountingPiece asset="cube" variant="sticker" size="calc(var(--tile) * 1.25)" floats={!blockSelected} lifted={blockSelected} ring={blockSelected ? accent : undefined} reduceMotion={reduceMotion} />
                </button>
              </motion.div>
            );
          })()}
        </div>
      )}
      <GhostLayer />
    </CountingRoot>
  );
};

export default CompareSetsGame;
